import { useMemo } from "react";
import { styled, Tooltip } from "@mui/material";
import { useFormikContext } from "formik";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { dashboardText } from "pages/AgronomistDashboard/_utils/shared";

const PROBABILITY_COLORS = {
  "0.00": "#D94B4B",
  0.25: "#F08B37",
  "0.50": "#E1BE40",
  0.75: "#9ABD54",
  "1.00": "#3E8E41",
};

const FALLBACK_COLOR = "#D6DEE3";

const AlertsWrap = styled("div")({
  minWidth: "250px",
  maxWidth: "360px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "@media (max-width:1100px)": {
    maxWidth: "100%",
  },
});

const AlertsBox = styled("div")({
  border: "1px solid #e2e7ec",
  backgroundColor: "#f8fbfd",
  borderRadius: "12px",
  padding: "6px 12px",
  minHeight: "80px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

const AlertsTitle = styled("div")({
  ...dashboardText,
  fontWeight: 500,
  color: "#5f6f78",
});

const AlertsStateText = styled("div")({
  fontSize: "12px",
  lineHeight: "16px",
  color: "#6a7d88",
});

const AlertsContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

const AlertsMessageContent = styled("div")({
  flex: 1,
  display: "flex",
  alignItems: "center",
});

const RowScroll = styled("div")({
  overflowX: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "#dde4e9 transparent",
  "&::-webkit-scrollbar": { height: "4px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#dde4e9",
    borderRadius: "999px",
  },
});

const GridRow = styled("div")({
  minWidth: "max-content",
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: "minmax(56px, 1fr)",
  gap: "8px",
});

const DateCell = styled("div")({
  textAlign: "center",
  fontSize: "12px",
  lineHeight: "16px",
  color: "#4f6570",
  whiteSpace: "nowrap",
});

const SquareCell = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ProbabilitySquare = styled("div", {
  shouldForwardProp: (prop) => prop !== "color",
})(({ color }) => ({
  width: "16px",
  height: "16px",
  borderRadius: "4px",
  backgroundColor: color,
  border: "1px solid #c9d3da",
}));

const normalizeProbability = (probability) => {
  if (probability === null || probability === undefined || probability === "") {
    return null;
  }

  const numeric =
    typeof probability === "number" ? probability : Number(probability);

  if (!Number.isFinite(numeric)) return null;

  return Number(numeric.toFixed(2));
};

const normalizeId = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const includesOperationId = (prediction, selectedOperationTypeId) => {
  const normalizedSelectedOperationTypeId = normalizeId(
    selectedOperationTypeId,
  );
  if (!normalizedSelectedOperationTypeId) return false;

  const operationIds = Array.isArray(prediction?.operationId)
    ? prediction.operationId
    : [];

  return operationIds.some(
    (operationId) =>
      normalizeId(operationId) === normalizedSelectedOperationTypeId,
  );
};

const resolvePrediction = ({
  predictions,
  selectedActionId,
  selectedOperationTypeId,
}) => {
  if (!predictions || !selectedOperationTypeId) return null;

  const normalizedSelectedActionId = normalizeId(selectedActionId);
  if (!normalizedSelectedActionId) return null;

  const actionPrediction = predictions[normalizedSelectedActionId];
  if (!actionPrediction) return null;

  return includesOperationId(actionPrediction, selectedOperationTypeId)
    ? actionPrediction
    : null;
};

const getProbabilityColor = (probability) => {
  const normalized = normalizeProbability(probability);
  if (normalized === null) return FALLBACK_COLOR;

  const key = normalized.toFixed(2);
  return PROBABILITY_COLORS[key] || FALLBACK_COLOR;
};

const ActionAlertsStrip = ({
  actionAlerts,
  isLoading = false,
  isError = false,
}) => {
  const { t, i18n } = useTranslation();
  const { values } = useFormikContext();
  const selectedActionId = normalizeId(values?.taskId);

  const selectedOperationTypeId = normalizeId(
    values?.waterAction?.operationTypeId ||
      values?.drugAction?.operationTypeId ||
      values?.fertilizeAction?.operationTypeId ||
      values?.diagnoseAction?.operationTypeId ||
      values?.otherAction?.operationTypeId ||
      values?.observationAction?.operationTypeId,
  );

  const dailyPredictions = useMemo(
    () =>
      Array.isArray(actionAlerts?.dailyPredictions)
        ? actionAlerts.dailyPredictions
        : [],
    [actionAlerts?.dailyPredictions],
  );

  const alertsByDay = useMemo(
    () =>
      dailyPredictions.map((dayPrediction) => {
        const prediction = resolvePrediction({
          predictions: dayPrediction?.predictions,
          selectedActionId,
          selectedOperationTypeId,
        });

        return {
          date: dayPrediction?.date,
          prediction,
          probability: normalizeProbability(prediction?.probability),
        };
      }),
    [dailyPredictions, selectedActionId, selectedOperationTypeId],
  );

  const shouldShowGrid =
    !isLoading &&
    !isError &&
    Boolean(selectedOperationTypeId) &&
    alertsByDay.length > 0;

  return (
    <AlertsWrap>
      <AlertsBox>
        <AlertsTitle>{t("ActionModal.Alerts.Title")}</AlertsTitle>
        {shouldShowGrid ? (
          <AlertsContent>
            <RowScroll>
              <GridRow>
                {alertsByDay.map((dayAlert, index) => (
                  <DateCell
                    key={`alert-date-${dayAlert.date || "na"}-${index}`}
                  >
                    {dayAlert?.date
                      ? moment(dayAlert.date).isSame(moment(), "day")
                        ? t("Global.Today")
                        : moment(dayAlert.date).format("DD/MM")
                      : "--/--"}
                  </DateCell>
                ))}
              </GridRow>
            </RowScroll>

            <RowScroll>
              <GridRow>
                {alertsByDay.map((dayAlert, index) => {
                  const language = i18n?.resolvedLanguage || i18n?.language;
                  const preferredDescription = language?.startsWith("el")
                    ? dayAlert?.prediction?.descriptionGr
                    : dayAlert?.prediction?.descriptionEn;
                  const description =
                    preferredDescription ||
                    dayAlert?.prediction?.descriptionEn ||
                    dayAlert?.prediction?.descriptionGr ||
                    t("ActionModal.Alerts.Tooltip.NoDescription");
                  const tooltipTitle = dayAlert?.prediction
                    ? description
                    : t("ActionModal.Alerts.Tooltip.NoPrediction");

                  return (
                    <SquareCell
                      key={`alert-probability-${dayAlert.date || "na"}-${index}`}
                    >
                      <Tooltip title={tooltipTitle} placement="top" arrow>
                        <ProbabilitySquare
                          color={getProbabilityColor(dayAlert?.probability)}
                        />
                      </Tooltip>
                    </SquareCell>
                  );
                })}
              </GridRow>
            </RowScroll>
          </AlertsContent>
        ) : (
          <AlertsMessageContent>
            <AlertsStateText>
              {!selectedOperationTypeId
                ? t("ActionModal.Alerts.SelectOperation")
                : isLoading
                  ? t("ActionModal.Alerts.SelectOperation")
                  : isError
                  ? t("ActionModal.Alerts.Unavailable")
                  : t("ActionModal.Alerts.NoData")}
            </AlertsStateText>
          </AlertsMessageContent>
        )}
      </AlertsBox>
    </AlertsWrap>
  );
};

export default ActionAlertsStrip;
